import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { translate, TranslocoService } from '@jsverse/transloco';

import { TabObject } from '@inmoment/the-kitchen';

import { AppService } from 'app/app.service';
import { Session } from 'app/providers/bridge-api/auth.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';
import {
  Dashboard,
  DASHBOARD_LEVEL_WIDGET_GROUP_ID,
} from '../../../../providers/bridge-api/dashboard.service';
import { AlertService } from '../../../../services/alert.service';
import { DialogService } from '../../../../services/dialog.service';

import { getDashboardLevelWidgetGroup } from '../../stores/widget-group.store';

export interface SaveDashboardEvent {
  dashboard: Dashboard;
  done: (value: unknown) => void;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-dashboard-options',
  templateUrl: './dashboard-options.component.html',
  styleUrls: ['./dashboard-options.component.scss'],
  standalone: false,
})
export class DashboardOptionsComponent implements OnInit, OnChanges, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() open: boolean;
  @Input() dashboard: Dashboard;
  @Input() isReadOnly: boolean;
  @Input() isHideVisible: boolean;
  @Input() session: Session;
  @Output() close = new EventEmitter();

  @Output() saveDashboard: EventEmitter<SaveDashboardEvent> = new EventEmitter();
  @Output() clearUserCache: EventEmitter<void> = new EventEmitter();
  @Output() saveAsDashboard: EventEmitter<void> = new EventEmitter();
  @Output() exportDashboard: EventEmitter<void> = new EventEmitter();
  @Output() restoreDashboard: EventEmitter<Dashboard> = new EventEmitter();

  isSaving = false;

  isValid = true;
  isValidating = false;
  isDashboardChanged = false;
  exposedSave = this.save.bind(this);
  dashboardCopy: Dashboard;
  tabs: TabObject[];
  activeTabId = 'general';

  constructor(
    private dialogService: DialogService,
    private alertService: AlertService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    AppService.hideBodyOverflow();
    this.dashboard.payload.isExecutive ??= false;
    this.dashboardCopy = JSON.parse(JSON.stringify(this.dashboard));
    const t = this.translocoService.translateObject('dashboard-options-component');
    this.tabs = [
      { id: 'general', title: t['general'] },
      { id: 'save-export', title: t['save-export'] },
      { id: 'permissions', title: t['permissions'] },
      { id: 'metadata', title: t['metadata'] },
      { id: 'history', title: t['history'] },
    ];
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.dashboard) {
      this.onDashboardChanged(changes.dashboard.currentValue);
      this.dashboardCopy = JSON.parse(JSON.stringify(this.dashboard));
    }
  }

  cancel() {
    if (this.isSaving) {
      return;
    }

    if (this.isDashboardChanged) {
      this.dialogService
        .showWarningPopup({
          title: translate('dashboard-options-component.cancel-check'),
        })
        .afterClosed()
        .subscribe((result) => {
          if (result) {
            this.close.emit();
          }
        });

      return;
    }

    this.close.emit();
  }

  public async save() {
    if (!this.isValid) {
      return;
    }
    this.isSaving = true;

    await new Promise((resolve) => {
      this.saveDashboard.emit({ dashboard: this.dashboardCopy, done: resolve });
    });

    this.dashboardCopy = { ...this.dashboardCopy };
    this.isSaving = false;
    this.isDashboardChanged = false;
    this.alertService.showToast(
      translate('dashboard-options-component.dashboard-saved', { name: this.dashboardCopy.title })
    );
  }

  public onDashboardChanged(newDashboard: Dashboard): void {
    // Shallow compare the dashboard payloads with skipping the arrays
    const payloadChanged = !Object.keys(this.dashboard.payload).every((key) => {
      if (Array.isArray(this.dashboard.payload[key])) {
        return true;
      }

      return this.dashboard.payload[key] === newDashboard.payload[key];
    });

    const titleChanged = this.dashboard.title !== newDashboard.title;
    /* eslint-disable eqeqeq */
    const lockedChanged = this.dashboard.locked != newDashboard.locked;
    const hiddenChanged = this.dashboard.visibility != newDashboard.visibility;
    /* eslint-enable eqeqeq */
    const widgetGroupChanged = !Utils.deepEquals(
      getDashboardLevelWidgetGroup(this.dashboard),
      getDashboardLevelWidgetGroup(newDashboard)
    );

    this.isDashboardChanged =
      payloadChanged || titleChanged || lockedChanged || hiddenChanged || widgetGroupChanged;
  }

  ngOnDestroy() {
    AppService.showBodyOverflow();
  }
}
